const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

export class ApiError extends Error {
  constructor(status, detail) {
    super(detail)
    this.status = status
  }
}

let tokenProvider = () => null
export const setTokenProvider = (fn) => {
  tokenProvider = fn
}

async function request(path, { method = 'GET', body, form } = {}) {
  const headers = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const token = await tokenProvider()
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(`${API_URL}/api/v1${path}`, {
    method,
    headers,
    body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const detail = Array.isArray(data.detail)
      ? data.detail.map((d) => `${d.loc?.slice(-1)[0] ?? ''}: ${d.msg}`).join('; ')
      : data.detail
    throw new ApiError(res.status, detail || res.statusText)
  }
  return res.status === 204 ? null : res.json()
}

const q = (params) => new URLSearchParams(Object.entries(params).filter(([, v]) => v != null)).toString()

export const api = {
  feeders: () => request('/feeders'),
  feeder: (slug) => request(`/feeders/${slug}`),
  events: (slug, limit = 50) => request(`/feeders/${slug}/events?limit=${limit}`),
  telemetry: (slug, hours = 24) => request(`/feeders/${slug}/telemetry?hours=${hours}`),
  moments: (slug) => request(`/feeders/${slug}/moments?${q({ hours: 24, kind: 'meal', limit: 8 })}`),
  feed: (slug) => request(`/feeders/${slug}/feed`, { method: 'POST' }),
  refresh: (slug) => request(`/feeders/${slug}/refresh`, { method: 'POST' }),
  cats: (feederSlug) => request(feederSlug ? `/cats?${q({ feeder: feederSlug })}` : '/cats'),
  me: () => request('/me'),
  follows: () => request('/me/follows'),
  follow: (target) => request('/me/follows', { method: 'POST', body: target }),
  unfollow: (id) => request(`/me/follows/${id}`, { method: 'DELETE' }),
  updateMe: (body) => request('/me', { method: 'PATCH', body }),
  deleteMe: () => request('/me', { method: 'DELETE' }),
  appConfig: () => request('/app/config'),
  ranks: () => request('/ranks'),
  myClips: (before) => request(`/me/clips?${q({ limit: 20, before })}`),
  clip: (id) => request(`/clips/${id}`),
  feederClips: (slug) => request(`/feeders/${slug}/clips?limit=12`),
  donationConfig: () => request('/donations/config'),
  checkout: (body) => request('/donations/checkout', { method: 'POST', body }),
  donationStatus: (id) => request(`/donations/${id}/status`),
  donationSummary: (feeder) => request(`/donations/summary?${q({ feeder })}`),
  myDonations: () => request('/me/donations'),
  billingPortal: () => request('/me/billing-portal', { method: 'POST' }),
  leaderboard: (period, city) => request(`/leaderboard?${q({ period, city })}`),
  recentFeeders: (slug) => request(`/feeders/${slug}/recent-feeders`),
  live: (slug, after) => request(`/feeders/${slug}/live?${q({ after })}`),
  react: (slug, kind) => request(`/feeders/${slug}/reactions`, { method: 'POST', body: { kind } }),
  chat: (slug, text) => request(`/feeders/${slug}/chat`, { method: 'POST', body: { text } }),
  report: (id) => request(`/chat/${id}/report`, { method: 'POST' }),
  catProfile: (id) => request(`/cats/${id}/profile`),
  reportCat: (id, body) => request(`/cats/${id}/reports`, { method: 'POST', body }),
  telegram: () => request('/me/telegram'),
  linkTelegram: () => request('/me/telegram/link', { method: 'POST' }),
  unlinkTelegram: () => request('/me/telegram', { method: 'DELETE' }),
  spots: () => request('/spots'),
  suggestSpot: (body) => request('/spots', { method: 'POST', body }),
  voteSpot: (id) => request(`/spots/${id}/vote`, { method: 'POST' }),
  unvoteSpot: (id) => request(`/spots/${id}/vote`, { method: 'DELETE' }),
  contact: (body) => request('/contact', { method: 'POST', body }),
  registerPush: (token) => request('/me/push-tokens', { method: 'POST', body: { token, platform: 'web' } }),
}

export const admin = {
  feeders: () => request('/admin/feeders'),
  feeder: (slug) => request(`/admin/feeders/${slug}`),
  create: (body) => request('/admin/feeders', { method: 'POST', body }),
  update: (slug, body) => request(`/admin/feeders/${slug}`, { method: 'PATCH', body }),
  remove: (slug) => request(`/admin/feeders/${slug}`, { method: 'DELETE' }),
  rotate: (slug) => request(`/admin/feeders/${slug}/credentials`, { method: 'POST' }),
  snapshot: (slug) => request(`/admin/feeders/${slug}/snapshot`, { method: 'POST' }),
  schedule: (slug) => request(`/admin/feeders/${slug}/schedule`),
  setSchedule: (slug, slots) => request(`/admin/feeders/${slug}/schedule`, { method: 'PUT', body: slots }),
  command: (slug, action, params = {}) =>
    request(`/admin/feeders/${slug}/commands`, { method: 'POST', body: { action, params } }),
  commands: (slug) => request(`/admin/feeders/${slug}/commands`),
  createCat: (body) => request('/admin/cats', { method: 'POST', body }),
  updateCat: (id, body) => request(`/admin/cats/${id}`, { method: 'PATCH', body }),
  deleteCat: (id) => request(`/admin/cats/${id}`, { method: 'DELETE' }),
  health: () => request('/admin/health'),
  updateSpot: (id, body) => request(`/admin/spots/${id}`, { method: 'PATCH', body }),
  expenses: () => request('/admin/expenses'),
  addExpense: (body) => request('/admin/expenses', { method: 'POST', body }),
  deleteExpense: (id) => request(`/admin/expenses/${id}`, { method: 'DELETE' }),
  hideMessage: (id) => request(`/admin/chat/${id}`, { method: 'DELETE' }),
  banAuthor: (id) => request(`/admin/chat/${id}/ban`, { method: 'POST' }),
  catReports: (resolved = false) => request(`/admin/cat-reports?resolved=${resolved}`),
  resolveReport: (id, resolution) => request(`/admin/cat-reports/${id}/resolve`, { method: 'POST', body: { resolution } }),
  restreams: (slug) => request(`/admin/feeders/${slug}/restreams`),
  addRestream: (slug, body) => request(`/admin/feeders/${slug}/restreams`, { method: 'POST', body }),
  updateRestream: (id, body) => request(`/admin/restreams/${id}`, { method: 'PATCH', body }),
  deleteRestream: (id) => request(`/admin/restreams/${id}`, { method: 'DELETE' }),
  clips: (slug) => request(`/admin/clips?${q({ feeder: slug, limit: 24 })}`),
  tagClip: (id, catIds) => request(`/admin/clips/${id}`, { method: 'PATCH', body: { cat_ids: catIds } }),
  deleteClip: (id) => request(`/admin/clips/${id}`, { method: 'DELETE' }),
  recordClip: (slug) => request(`/admin/feeders/${slug}/clips`, { method: 'POST' }),
  upload: (file) => {
    const form = new FormData()
    form.append('file', file)
    return request('/admin/uploads', { method: 'POST', form })
  },
}
